import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { api } from '../lib/api'
import { DESTINOS, GRUPOS, ir } from '../lib/navegacao'
import './busca.css'

/**
 * A busca global (⌘K / Ctrl+K).
 *
 * ─── POR QUE ELA É METADE DA RESPOSTA AO "CADÊ TUDO" ──────────────
 *
 * Sem termo digitado, ela mostra **todos os destinos**. É o mapa do
 * produto numa tecla. Quem nunca achou o repasse porque ele morava no
 * fim de uma página que já era o fim de outra, acha aqui.
 *
 * ─── O ESCOPO MANDA, E ANTES DA CHAMADA ───────────────────────────
 *
 * Grupo cuja rota a pessoa não pode chamar **nem é consultado**. Não é
 * só esconder o resultado: `exigirEscopo` responde 404, e disparar a
 * chamada para descartar a resposta gastaria rede e encheria o log de
 * negativas que não significam nada.
 */

type Achado = {
  grupo: string
  rotulo: string
  detalhe?: string | undefined
  ir(): void
}

const debounce = 220

export function Busca({ pode, aoFechar }: { pode(e: string): boolean; aoFechar(): void }) {
  const [termo, setTermo] = useState('')
  const [remotos, setRemotos] = useState<Achado[]>([])
  const [buscando, setBuscando] = useState(false)
  const [cursor, setCursor] = useState(0)
  const campo = useRef<HTMLInputElement>(null)

  useEffect(() => { campo.current?.focus() }, [])

  /* ── destinos: sempre disponíveis, sem rede ────────────────────── */
  const destinos = useMemo<Achado[]>(() => {
    const t = termo.trim().toLowerCase()
    return DESTINOS
      .filter((d) => !d.escopo || pode(d.escopo))
      .filter((d) => !t || d.rotulo.toLowerCase().includes(t))
      .map((d) => ({
        grupo: 'Ir para',
        rotulo: d.rotulo,
        detalhe: GRUPOS.find((g) => g.id === d.grupo)?.titulo,
        ir: () => { ir(d.caminho); aoFechar() },
      }))
  }, [termo, pode, aoFechar])

  /* ── cadastros: só com termo, e só o que o escopo permite ──────── */
  const carregar = useCallback(async (t: string) => {
    if (t.length < 2) { setRemotos([]); return }
    setBuscando(true)
    const nada = () => null
    try {
      const [pac, prof, proc] = await Promise.all([
        pode('paciente:read')
          ? api<{ itens: Array<{ id: string; nome: string; telefone: string | null }> }>(
              `/v1/pacientes?busca=${encodeURIComponent(t)}`).catch(nada) : null,
        api<{ itens: Array<{ id: string; nome: string; conselho: string | null }> }>(
          '/v1/profissionais').catch(nada),
        api<{ itens: Array<{ id: string; nome: string }> }>('/v1/procedimentos').catch(nada),
      ])
      const baixo = t.toLowerCase()
      setRemotos([
        ...(pac?.itens ?? []).slice(0, 6).map((x) => ({
          grupo: 'Pacientes', rotulo: x.nome, detalhe: x.telefone ?? undefined,
          ir: () => { ir(`/pacientes/${x.id}`); aoFechar() },
        })),
        ...(prof?.itens ?? []).filter((x) => x.nome.toLowerCase().includes(baixo)).slice(0, 4).map((x) => ({
          grupo: 'Profissionais', rotulo: x.nome, detalhe: x.conselho ?? undefined,
          ir: () => { ir('/equipe'); aoFechar() },
        })),
        ...(proc?.itens ?? []).filter((x) => x.nome.toLowerCase().includes(baixo)).slice(0, 4).map((x) => ({
          grupo: 'Procedimentos', rotulo: x.nome,
          ir: () => { ir('/ajustes'); aoFechar() },
        })),
      ])
    } finally { setBuscando(false) }
  }, [pode, aoFechar])

  useEffect(() => {
    const t = setTimeout(() => void carregar(termo.trim()), debounce)
    return () => clearTimeout(t)
  }, [termo, carregar])

  const tudo = useMemo(() => [...destinos, ...remotos], [destinos, remotos])
  useEffect(() => { setCursor(0) }, [termo])

  const porGrupo = useMemo(() => {
    const m = new Map<string, Achado[]>()
    for (const a of tudo) { const l = m.get(a.grupo) ?? []; l.push(a); m.set(a.grupo, l) }
    return [...m.entries()]
  }, [tudo])

  function teclado(e: React.KeyboardEvent) {
    if (e.key === 'Escape') { aoFechar(); return }
    if (e.key === 'ArrowDown') { e.preventDefault(); setCursor((c) => Math.min(c + 1, tudo.length - 1)) }
    if (e.key === 'ArrowUp') { e.preventDefault(); setCursor((c) => Math.max(c - 1, 0)) }
    if (e.key === 'Enter') { e.preventDefault(); tudo[cursor]?.ir() }
  }

  let indice = -1

  return (
    <>
      <div className="busca__fundo" onClick={aoFechar} />
      <div className="busca" role="dialog" aria-modal="true" aria-label="Buscar no sistema">
        <input ref={campo} className="busca__campo" value={termo}
               placeholder="Paciente, profissional, procedimento ou uma tela…"
               aria-label="O que você procura"
               onChange={(e) => setTermo(e.target.value)}
               onKeyDown={teclado} />

        <div className="busca__resultados" role="listbox">
          {porGrupo.map(([grupo, itens]) => (
            <div key={grupo} className="busca__grupo">
              <h3>{grupo}</h3>
              {itens.map((a) => {
                indice += 1
                const i = indice
                return (
                  <button key={`${grupo}-${a.rotulo}-${i}`}
                          role="option" aria-selected={i === cursor}
                          className={`busca__item${i === cursor ? ' is-cursor' : ''}`}
                          onMouseEnter={() => setCursor(i)}
                          onClick={a.ir}>
                    <span className="busca__rotulo">{a.rotulo}</span>
                    {a.detalhe && <span className="busca__detalhe">{a.detalhe}</span>}
                  </button>
                )
              })}
            </div>
          ))}

          {tudo.length === 0 && (
            <p className="busca__vazio">
              {buscando ? 'Procurando…' : <>Nada com “{termo}”.</>}
            </p>
          )}
        </div>

        <footer className="busca__pe">
          <span><kbd>↑</kbd><kbd>↓</kbd> navegar</span>
          <span><kbd>↵</kbd> abrir</span>
          <span><kbd>esc</kbd> fechar</span>
        </footer>
      </div>
    </>
  )
}
